import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { TeachersApi } from "@/openapi/generated";
import type { UpdateTeacherRequest } from "@/openapi/generated/models";

export const useTeacherUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (updateTeacherRequest: UpdateTeacherRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(TeachersApi, accessToken);
      return service.apiTeachersPut({
        updateTeacherRequest,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Teachers] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Teacher] });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.EntityChangeHistory],
      });
      toast({
        title: "Teacher updated",
        description: "Teacher updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to update teacher",
      );

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
